import React, { useState } from "react";
import { Post, PostStatus, VALID_STATUSES } from "@/core/types/postTypes";
import { usePostStatus } from "./hooks/usePostStatus";
import { getDropdownStyles, getStatusConfig } from "@/core/utils/statusConfig";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { OptionDropdown } from "@/components/ui/OptionDropdown";
import type { DropdownOption } from "@/components/ui/OptionDropdown";

interface PostStatusDropdownProps {
  post: Post;
  onStatusChange?: (post: Post, newStatus: PostStatus, response: any) => void;
  disabled?: boolean;
}

const OPTIONS: DropdownOption<PostStatus>[] = VALID_STATUSES.map((status) => {
  const styles = getDropdownStyles(status);
  return {
    value: status,
    label: getStatusConfig(status).label,
    bg: styles.bg,
    text: styles.text,
    dot: styles.dot,
  };
});

export const PostStatusDropdown: React.FC<PostStatusDropdownProps> = ({
  post,
  onStatusChange,
  disabled = false,
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<PostStatus | null>(null);

  const { updateStatus, isUpdating } = usePostStatus({
    onStatusUpdate: (response) => {
      onStatusChange?.(post, response.newStatus as PostStatus, response);
    },
    onError: (error) => {
      console.error("Status update failed:", error);
    },
  });

  const handleStatusChange = async (newStatus: PostStatus) => {
    if (newStatus === post.status || isUpdating) return;

    // PUBLISHED → SCHEDULED is rescheduling (allowed, gentle confirmation)
    const isRescheduleFromPublished =
      post.status === PostStatus.PUBLISHED && newStatus === PostStatus.SCHEDULED;

    // Backward transition (SCHEDULED/PUBLISHED → anything else) loses the
    // active scheduling, so it needs a warning confirmation.
    const isBackwardTransition =
      (post.status === PostStatus.SCHEDULED || post.status === PostStatus.PUBLISHED) &&
      newStatus !== PostStatus.SCHEDULED &&
      newStatus !== PostStatus.PUBLISHED &&
      !isRescheduleFromPublished;

    if (isBackwardTransition || isRescheduleFromPublished) {
      setPendingStatus(newStatus);
      setShowConfirmDialog(true);
    } else {
      await updateStatus(post.id, newStatus);
    }
  };

  const handleConfirmStatusChange = async () => {
    if (pendingStatus) {
      setShowConfirmDialog(false);
      await updateStatus(post.id, pendingStatus);
      setPendingStatus(null);
    }
  };

  const handleCancelStatusChange = () => {
    setShowConfirmDialog(false);
    setPendingStatus(null);
  };

  return (
    <>
      <OptionDropdown
        value={post.status}
        options={OPTIONS}
        onChange={handleStatusChange}
        disabled={disabled}
        loading={isUpdating}
      />

      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={handleCancelStatusChange}
        onConfirm={handleConfirmStatusChange}
        title={`Cambiare stato in ${pendingStatus ? getStatusConfig(pendingStatus).label : ""}?`}
        message={
          post.status === PostStatus.PUBLISHED && pendingStatus === PostStatus.SCHEDULED
            ? `Questo post è già stato pubblicato. Cambiando lo stato a "${getStatusConfig(PostStatus.SCHEDULED).label}" potrai riprogrammarlo per una nuova pubblicazione. Lo storico della pubblicazione originale verrà mantenuto.`
            : post.status === PostStatus.SCHEDULED
              ? `Questo post è attualmente programmato per la pubblicazione. Cambiando lo stato a "${pendingStatus ? getStatusConfig(pendingStatus).label : ""}" la programmazione attiva verrà annullata. Potrai riprogrammarlo in seguito se necessario.`
              : `Questo post è già stato pubblicato. Cambiando lo stato a "${pendingStatus ? getStatusConfig(pendingStatus).label : ""}" verrà contrassegnato come non pubblicato e i dati di programmazione verranno rimossi. Questa azione non può essere annullata.`
        }
        confirmText={
          post.status === PostStatus.PUBLISHED && pendingStatus === PostStatus.SCHEDULED
            ? "Riprogramma"
            : "Cambia stato"
        }
        variant={
          post.status === PostStatus.PUBLISHED && pendingStatus === PostStatus.SCHEDULED
            ? "info"
            : "warning"
        }
      />
    </>
  );
};
